import { useState } from 'react'
import { useSearchUsers, useSendInvitation, useLeagueInvitations, useSendEmailInvitation } from '../../hooks/useInvitations'
import { useConnections } from '../../hooks/useConnections'
import { toast } from '../ui/Toast'
import Avatar from '../ui/Avatar'
import { buildJoinLink } from '../../lib/shareLink'

const STATUS_STYLES = {
  pending: 'text-accent',
  accepted: 'text-correct',
  declined: 'text-text-muted',
}

export default function InvitePlayerModal({ leagueId, inviteCode, leagueName, format, memberIds = [], initialUsername = null, onClose }) {
  const [query, setQuery] = useState(initialUsername || '')
  const [email, setEmail] = useState('')
  const [linkCopied, setLinkCopied] = useState(false)
  const { data: searchResults } = useSearchUsers(query)
  const { data: invitations } = useLeagueInvitations(leagueId)
  const { data: connections } = useConnections()
  const sendInvitation = useSendInvitation()
  const sendEmailInvitation = useSendEmailInvitation()

  const inviteLink = buildJoinLink(inviteCode, { isBracket: format === 'bracket' })

  async function handleInvite(username) {
    try {
      await sendInvitation.mutateAsync({ leagueId, username })
      toast(`Invite sent to @${username}`, 'success')
      setQuery('')
    } catch (err) {
      toast(err.message || 'Failed to send invite', 'error')
    }
  }

  async function handleInviteAll() {
    // Snapshot the available list up front so items filtering out mid-loop
    // (as invitations return and invitedUsernames re-derives) don't skip
    // anyone. Fire in parallel with allSettled — one bad row shouldn't
    // block the rest.
    const targets = availableConnections.map((c) => c.username).filter(Boolean)
    if (!targets.length) return
    const results = await Promise.allSettled(
      targets.map((u) => sendInvitation.mutateAsync({ leagueId, username: u }))
    )
    const ok = results.filter((r) => r.status === 'fulfilled').length
    const fail = results.length - ok
    if (fail === 0) toast(`Invited ${ok} ${ok === 1 ? 'player' : 'players'}`, 'success')
    else if (ok === 0) toast(`Failed to invite ${fail} ${fail === 1 ? 'player' : 'players'}`, 'error')
    else toast(`Invited ${ok}, ${fail} failed`, 'info')
  }

  async function handleEmailInvite(e) {
    e.preventDefault()
    if (!email.trim()) return
    try {
      await sendEmailInvitation.mutateAsync({ leagueId, email: email.trim() })
      toast(`Invite sent to ${email.trim()}`, 'success')
      setEmail('')
    } catch (err) {
      toast(err.message || 'Failed to send email', 'error')
    }
  }

  async function handleCopyLink() {
    await navigator.clipboard.writeText(inviteLink)
    setLinkCopied(true)
    toast('Link copied!', 'success')
    setTimeout(() => setLinkCopied(false), 2000)
  }

  const pendingInvites = (invitations || []).filter((i) => i.status === 'pending')
  const pastInvites = (invitations || []).filter((i) => i.status !== 'pending')

  // Filter connections: exclude users already invited or already members.
  // Connections come back keyed by `user_id` (not `id`), so the previous
  // `memberIdSet.has(c.id)` always returned false and members slipped
  // through the filter.
  const invitedUsernames = new Set((invitations || []).map((i) => i.user?.username))
  const memberIdSet = new Set(memberIds)
  const availableConnections = (connections || []).filter((c) => !invitedUsernames.has(c.username) && !memberIdSet.has(c.user_id))

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center px-0 md:px-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative bg-bg-primary border border-text-primary/20 w-full md:max-w-md rounded-t-2xl md:rounded-2xl p-6 max-h-[90vh] md:max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-text-muted hover:text-text-primary text-xl leading-none"
        >
          &times;
        </button>

        <h2 className="font-display text-xl mb-4">Invite Player</h2>

        {/* Copy Invite Link */}
        <div className="mb-4">
          <h3 className="text-xs text-text-muted uppercase tracking-wider mb-2">Invite Link</h3>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-bg-input border border-border rounded-lg px-3 py-2.5 text-sm text-text-secondary truncate select-all">
              {inviteLink}
            </div>
            <button
              onClick={handleCopyLink}
              className="px-3 py-2.5 rounded-lg text-xs font-semibold bg-accent text-white hover:bg-accent-hover transition-colors flex-shrink-0"
            >
              {linkCopied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>

        <div className="border-t border-border mb-4" />

        {/* Invite by Email */}
        <div className="mb-4">
          <h3 className="text-xs text-text-muted uppercase tracking-wider mb-2">Invite by Email</h3>
          <form onSubmit={handleEmailInvite} className="flex items-center gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="friend@example.com"
              className="flex-1 bg-bg-input border border-border rounded-lg px-4 py-2.5 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-accent"
            />
            <button
              type="submit"
              disabled={!email.trim() || sendEmailInvitation.isPending}
              className="px-3 py-2.5 rounded-lg text-xs font-semibold bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 flex-shrink-0"
            >
              {sendEmailInvitation.isPending ? 'Sending...' : 'Send'}
            </button>
          </form>
        </div>

        <div className="border-t border-border mb-4" />

        {/* Invite by Username */}
        <h3 className="text-xs text-text-muted uppercase tracking-wider mb-2">Invite by Username</h3>

        {/* Search Input */}
        <div className="relative mb-4">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by username..."
            className="w-full bg-bg-input border border-border rounded-lg px-4 py-3 text-text-primary placeholder-text-muted focus:outline-none focus:border-accent"
          />

          {/* Search Results Dropdown */}
          {query.length >= 2 && searchResults?.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-bg-card border border-border rounded-xl shadow-lg z-10 overflow-hidden">
              {searchResults.map((user) => (
                <button
                  key={user.id}
                  onClick={() => handleInvite(user.username)}
                  disabled={sendInvitation.isPending}
                  className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-bg-card-hover transition-colors disabled:opacity-50"
                >
                  <Avatar user={user} size="lg" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{user.display_name || user.username}</div>
                    <div className="text-xs text-text-muted">@{user.username}</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {query.length >= 2 && searchResults?.length === 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-bg-card border border-border rounded-xl shadow-lg z-10 px-4 py-3 text-sm text-text-muted">
              No users found
            </div>
          )}
        </div>

        {/* Your Connections */}
        {availableConnections.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs text-text-muted uppercase tracking-wider">Your Squad</h3>
              {availableConnections.length > 1 && (
                <button
                  onClick={handleInviteAll}
                  disabled={sendInvitation.isPending}
                  className="text-xs font-semibold text-accent hover:text-accent-hover transition-colors disabled:opacity-50"
                >
                  {sendInvitation.isPending ? 'Inviting…' : `Invite All (${availableConnections.length})`}
                </button>
              )}
            </div>
            <div className="space-y-1 mb-3">
              {availableConnections.map((conn) => (
                <div
                  key={conn.user_id}
                  className="flex items-center justify-between px-3 py-2 rounded-lg bg-bg-secondary"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Avatar user={conn} size="sm" />
                    <span className="text-sm truncate">@{conn.username}</span>
                  </div>
                  <button
                    onClick={() => handleInvite(conn.username)}
                    disabled={sendInvitation.isPending}
                    className="py-1 px-3 rounded-lg text-xs font-semibold bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 flex-shrink-0"
                  >
                    Invite
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pending Invites */}
        {pendingInvites.length > 0 && (
          <div className="mb-4">
            <h3 className="text-xs text-text-muted uppercase tracking-wider mb-2">Pending</h3>
            <div className="space-y-1">
              {pendingInvites.map((invite) => (
                <div
                  key={invite.id}
                  className="flex items-center justify-between px-3 py-2 rounded-lg bg-bg-secondary"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Avatar user={invite.user} size="sm" />
                    <span className="text-sm truncate">@{invite.user?.username}</span>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded ${STATUS_STYLES.pending}`}>
                    Pending
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Past Invites */}
        {pastInvites.length > 0 && (
          <div>
            <h3 className="text-xs text-text-muted uppercase tracking-wider mb-2">History</h3>
            <div className="space-y-1">
              {pastInvites.map((invite) => (
                <div
                  key={invite.id}
                  className="flex items-center justify-between px-3 py-2 rounded-lg bg-bg-secondary opacity-60"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Avatar user={invite.user} size="sm" />
                    <span className="text-sm truncate">@{invite.user?.username}</span>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded ${STATUS_STYLES[invite.status]}`}>
                    {invite.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
